import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Hotel, Check, Circle, AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

export type GuestBootstrapStage =
  | "loading"
  | "validating"
  | "preparing"
  | "error"
  | "complete";

interface Step {
  id: GuestBootstrapStage;
  labelKey: string;
  defaultLabel: string;
}

const STEPS: Step[] = [
  {
    id: "validating",
    labelKey: "guest_bootstrap_validating",
    defaultLabel: "Validating access",
  },
  {
    id: "preparing",
    labelKey: "guest_bootstrap_preparing",
    defaultLabel: "Preparing your stay",
  },
];

interface GuestBootstrapOverlayProps {
  stage: GuestBootstrapStage;
  errorMessage?: string;
  onRetry?: () => void;
}

/**
 * GuestBootstrapOverlay - Full-screen overlay shown during guest app initialization.
 * Shows a step-based progress indicator for validation and settings loading.
 * Styled with grey background and blue accents for guest branding.
 */
const GuestBootstrapOverlay: React.FC<GuestBootstrapOverlayProps> = ({
  stage,
  errorMessage,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<GuestBootstrapStage>>(
    new Set()
  );

  // Delay showing content slightly for smoother initial render
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Track completed steps based on stage progression
  useEffect(() => {
    if (stage === "complete" || stage === "error") return;

    const stageOrder: GuestBootstrapStage[] = ["loading", "validating", "preparing"];
    const currentIndex = stageOrder.indexOf(stage);

    // Mark all previous stages as completed
    if (currentIndex > 0) {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev);
        for (let i = 0; i < currentIndex; i++) {
          newSet.add(stageOrder[i]);
        }
        return newSet;
      });
    }
  }, [stage]);

  // Reset retry state when stage changes
  useEffect(() => {
    if (stage !== "error") {
      setIsRetrying(false);
    }
  }, [stage]);

  const handleRetry = () => {
    setIsRetrying(true);
    onRetry?.();
  };

  const getStepStatus = useMemo(() => {
    return (stepId: GuestBootstrapStage): "completed" | "current" | "pending" => {
      if (completedSteps.has(stepId)) return "completed";
      // "loading" stage maps to "validating" step for display
      if (stage === "loading" && stepId === "validating") return "current";
      if (stepId === stage) return "current";
      return "pending";
    };
  }, [completedSteps, stage]);

  // Don't render if complete
  if (stage === "complete") return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-100 flex flex-col items-center justify-center",
        "bg-slate-50 dark:bg-slate-900"
      )}
    >
      {/* Content container with animation */}
      <div
        className={cn(
          "flex flex-col items-center text-center px-6",
          "transition-all duration-500",
          showContent ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
      >
        {stage === "error" ? (
          <>
            {/* Error icon */}
            <div className="relative mb-6">
              <AlertTriangle className="h-16 w-16 text-red-500" />
            </div>

            {/* Error title */}
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              {t("guest_bootstrap_error_title", "Access Error")}
            </h1>

            {/* Error message */}
            <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-sm">
              {errorMessage ||
                t(
                  "guest_bootstrap_error_message",
                  "Unable to validate your access. Please try again."
                )}
            </p>

            {/* Retry button */}
            {onRetry && (
              <Button
                onClick={handleRetry}
                disabled={isRetrying}
                className="gap-2 bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600"
              >
                <RefreshCw
                  className={cn("h-4 w-4", isRetrying && "animate-spin")}
                />
                {isRetrying
                  ? t("guest_bootstrap_retrying", "Retrying...")
                  : t("guest_bootstrap_retry", "Try Again")}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Hotel icon with pulse animation */}
            <div className="relative mb-8">
              <div className="h-20 w-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Hotel className="h-10 w-10 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="absolute inset-0 animate-ping opacity-30">
                <div className="h-20 w-20 rounded-full bg-blue-400 dark:bg-blue-500" />
              </div>
            </div>

            {/* Loading text */}
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-8">
              {t("guest_bootstrap_loading", "Loading your stay")}
            </h1>

            {/* Step indicator */}
            <div className="flex flex-col gap-3 w-56">
              {STEPS.map((step) => {
                const status = getStepStatus(step.id);
                return (
                  <div
                    key={step.id}
                    className={cn(
                      "flex items-center gap-3 transition-all duration-300",
                      status === "completed" && "opacity-60",
                      status === "pending" && "opacity-40"
                    )}
                  >
                    {/* Step indicator icon */}
                    <div
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-300",
                        status === "completed" &&
                          "bg-blue-600 dark:bg-blue-400 text-white",
                        status === "current" &&
                          "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-2 border-blue-600 dark:border-blue-400",
                        status === "pending" &&
                          "bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500"
                      )}
                    >
                      {status === "completed" ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : status === "current" ? (
                        <Circle className="h-2.5 w-2.5 fill-current animate-pulse" />
                      ) : (
                        <Circle className="h-2 w-2" />
                      )}
                    </div>

                    {/* Step label */}
                    <span
                      className={cn(
                        "text-sm font-medium transition-all duration-300",
                        status === "completed" &&
                          "text-slate-500 dark:text-slate-500 line-through",
                        status === "current" &&
                          "text-slate-900 dark:text-slate-100",
                        status === "pending" &&
                          "text-slate-400 dark:text-slate-500"
                      )}
                    >
                      {t(step.labelKey, step.defaultLabel)}
                      {status === "current" && (
                        <span className="inline-flex ml-1">
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          >
                            .
                          </span>
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          >
                            .
                          </span>
                          <span
                            className="animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          >
                            .
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GuestBootstrapOverlay;
